<template>
  <div v-if="isValidEntityType && isAdmin" :key="`${entityType}-${entitySubType || ''}`" class="d-flex flex-column p-4">
    <div class="update-phones-header d-flex flex-row align-items-center my-2">
      <b-button
        pill
        variant="transparent"
        :disabled="processing"
        class="update-phones-close-button btn-sm"
        @click="onCloseClick"
      >
        <b-icon icon="x" />
      </b-button>

      <b-button pill variant="primary" :disabled="isPrevDisabled" class="btn-sm" @click="onPrevClick">
        <b-icon icon="arrow-left" />
      </b-button>

      <span class="ml-4 mr-2">Per page:</span>
      <b-select
        v-model="perPage"
        pill
        :options="perPageOptions"
        :disabled="processing"
        class="btn-sm rounded-pill btn-primary update-phones-per-page-selector"
      />

      <div class="flex-grow-1 mx-4 d-flex flex-row justify-content-center">
        <div class="update-phones-page-buttons-before d-flex flex-row justify-content-end">
          <b-button
            v-for="pageIndex in pagesBefore"
            :key="pageIndex"
            pill
            variant="primary"
            :disabled="processing"
            class="btn-sm update-phones-page-button m-1"
            @click="onPageClick(pageIndex)"
          >
            {{ pageIndex + 1 }}
          </b-button>
        </div>

        <b-select
          v-model="page"
          pill
          :options="allPages"
          :disabled="processing"
          class="btn-sm rounded-pill btn-primary update-phones-page-selector m-1"
        />

        <div class="update-phones-page-buttons-after d-flex flex-row justify-content-start">
          <b-button
            v-for="pageIndex in pagesAfter"
            :key="pageIndex"
            pill
            variant="primary"
            :disabled="processing"
            class="btn-sm update-phones-page-button m-1"
            @click="onPageClick(pageIndex)"
          >
            {{ pageIndex + 1 }}
          </b-button>
        </div>
      </div>

      <b-button pill variant="primary" :disabled="isNextDisabled" class="btn-sm" @click="onNextClick">
        <b-icon icon="arrow-right" />
      </b-button>
    </div>

    <div class="update-phones-actions d-flex flex-row align-items-start my-2">
      <div class="update-phones-actions-title flex-grow-0 mr-2" />
      <div class="update-phones-actions-field flex-grow-0 mx-2">
        <b-button pill :disabled="processing" variant="success" class="btn-sm" @click="onSetAllSwedenClick">
          {{ $t('Set all empty to Sweden') }}
        </b-button>
      </div>
      <div class="update-phones-actions-original flex-grow-1 mx-2">{{ $t('Original value') }}</div>
      <div class="update-phones-actions-updated flex-grow-0 mx-2">
        <b-button pill :disabled="processing" variant="success" class="btn-sm" @click="onToggleAllDifferentClick">
          {{ $t('Select all different') }}
        </b-button>
      </div>
      <div class="update-phones-actions-actions flex-grow-0 ml-2">
        <b-button pill :disabled="processing" variant="success" class="btn-sm" @click="onSaveAllSelectedClick">
          {{ $t('Save all selected') }}
        </b-button>
      </div>
    </div>

    <update-phones-row
      v-for="item in pageItems"
      :key="item._id"
      :ref="getFieldRefName(item._id)"
      :item="item"
      :selected="selected[item._id]"
      :entity-type="entityType"
      :processing="processing"
      :processing-id="processingId"
      @select="onSelect"
      @save="onSave"
    />
  </div>

  <div v-else-if="entityType == null"><b-spinner large variant="primary" /></div>

  <div v-else>No data for {{ entityType }}</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import UpdatePhonesRow from './UpdatePhonesRow';
import getRef from '@/utils/getRef';

// +46 73 442 51 10
const commonSvPhoneRegexp = /^\+46 \d{2} \d{3} \d{2} \d{2}$/;

export default {
  name: 'UpdatePhones',

  components: { UpdatePhonesRow },

  data() {
    return {
      entityType: null,
      entitySubType: null,
      page: 0,
      perPage: 15,
      selected: {},
      processing: false,
      processingId: null,
      perPageOptions: [10, 15, 20, 30, 50, 100],
    };
  },

  computed: {
    ...mapGetters(['companies', 'persons', 'isAdmin']),

    isValidEntityType() {
      return !!this.entityType && ['company', 'person'].includes(this.entityType);
    },

    items() {
      const result = [];

      if (this.entityType === 'company') {
        if (this.companies) {
          this.companies.forEach(({ _id, phone, companyName }) => {
            if (this.phoneRequiresCheck(phone)) {
              result.push({ _id, phone, name: companyName });
            }
          });
        }

        return result;
      }

      if (this.entityType !== 'person') {
        return result;
      }

      const companyPersons = this.persons();
      if (!companyPersons) {
        return result;
      }

      const companies = this.companies || [];
      Object.entries(companyPersons).forEach(([companyId, persons]) => {
        const company = companies.find(({ _id }) => _id === companyId);

        persons.forEach(({ _id, phone, nextOfKinPhone, supervisorPhone, firstName, lastName, name, email }) => {
          const usedPhone =
            this.entitySubType === 'supervisor'
              ? supervisorPhone
              : this.entitySubType === 'relative'
              ? nextOfKinPhone
              : phone;

          if (!this.phoneRequiresCheck(usedPhone)) {
            return;
          }

          let item = result.find((entry) => entry._id === _id);
          if (!item) {
            item = {
              _id,
              phone: usedPhone,
              name: [firstName, lastName].filter(Boolean).join(' ') || name || email || '[name and email missing]',
              companies: [],
            };
            result.push(item);
          }

          if (company && !item.companies.some((entry) => entry._id === company._id)) {
            item.companies.push({ _id: company._id, name: company.companyName });
          }
        });
      });

      return result;
    },

    start() {
      return this.page * this.perPage;
    },

    pagesCount() {
      return Math.ceil(this.items.length / this.perPage);
    },

    allPages() {
      const result = [];
      for (let i = 0; i < this.pagesCount; i++) {
        result.push({ value: i, text: i + 1 });
      }

      return result;
    },

    pageItems() {
      return this.items.slice(this.start, this.start + this.perPage);
    },

    pagesBefore() {
      const last = Math.max(0, this.page - 3);
      const result = [];
      for (let i = this.page - 1; i >= last; i--) {
        result.unshift(i);
      }

      return result;
    },

    pagesAfter() {
      const last = Math.min(this.pagesCount - 1, this.page + 3);
      const result = [];
      for (let i = this.page + 1; i <= last; i++) {
        result.push(i);
      }

      return result;
    },

    isPrevDisabled() {
      return this.processing || this.start <= 0;
    },

    isNextDisabled() {
      return this.processing || this.page >= this.pagesCount;
    },
  },

  methods: {
    ...mapActions(['saveCompanyProperties', 'savePersonProperties']),

    phoneRequiresCheck(phone) {
      return !!phone && !commonSvPhoneRegexp.test(phone);
    },

    getFieldRefName(id) {
      return `phone-${id}`;
    },

    getFieldRef(id) {
      return getRef(this.$refs, this.getFieldRefName(id));
    },

    async updateCompanyPhone(item, phone) {
      const company = this.companies.find(({ _id }) => _id === item._id);
      if (company) {
        await this.saveCompanyProperties({ updateCompany: company, newCompanyProperties: { phone } });
      }
    },

    async updatePersonPhone(item, phone) {
      let person = null;
      Object.values(this.persons()).some((persons) => {
        person = persons.find(({ _id }) => _id === item._id);
        return !!person;
      });

      const field =
        this.entitySubType === 'supervisor'
          ? 'supervisorPhone'
          : this.entitySubType === 'relative'
          ? 'nextOfKinPhone'
          : 'phone';
      if (person) {
        await this.savePersonProperties({ updatePerson: person, newPersonProperties: { [field]: phone } });
      }
    },

    async saveItem(id) {
      const item = this.items.find(({ _id }) => id === _id);
      if (!item) {
        return;
      }

      try {
        this.processing = true;
        this.processingId = item._id;
        // Not the best solution. I just don't wan't to maintain list of changed values in this component too.
        // This component exists for dev purposes mostly anyway.
        const ref = this.getFieldRef(item._id);
        const phone = (ref && ref.innerValue) || '';
        if (this.entityType === 'company') {
          await this.updateCompanyPhone(item, phone);
        } else if (this.entityType === 'person') {
          await this.updatePersonPhone(item, phone);
        }
      } catch (error) {
        console.error(`Failed to update ${this.entityType} phone`, item, error);
      } finally {
        this.processingId = null;
        this.processing = false;
      }
    },

    onPrevClick() {
      this.page--;
    },

    onNextClick() {
      this.page++;
    },

    onPageClick(pageNumber) {
      this.page = pageNumber;
    },

    onSelect(id, value) {
      const item = this.items.find(({ _id }) => id === _id);
      if (item) {
        this.selected[id] = value;
      }
    },

    onSetAllSwedenClick() {
      this.pageItems.forEach(({ _id }) => {
        const ref = this.getFieldRef(_id);
        if (ref && ref.setLocaleIfEmpty) {
          ref.setLocaleIfEmpty('sv');
        }
      });
    },

    onToggleAllDifferentClick() {
      this.items.forEach(({ _id }) => {
        const ref = this.getFieldRef(_id);
        this.selected[_id] = !!(ref && ref.isDifferent);
      });
    },

    onSave(id) {
      this.saveItem(id);
    },

    async onSaveAllSelectedClick() {
      const ids = Object.entries(this.selected)
        .filter(([, selected]) => selected)
        .map(([id]) => id);
      for (let i = 0; i < ids.length; i++) {
        await this.saveItem(ids[i]);
      }
    },

    onCloseClick() {
      this.$router.push({ name: 'OrganisationOverview' });
    },
  },

  watch: {
    $route: {
      immediate: true,
      async handler(next) {
        const entityType = (next && next.params.entityType) || null;
        if (entityType === 'person-supervisor') {
          this.entityType = 'person';
          this.entitySubType = 'supervisor';
        } else if (entityType === 'person-relative') {
          this.entityType = 'person';
          this.entitySubType = 'relative';
        } else {
          this.entityType = entityType;
          this.entitySubType = null;
        }
      },
    },

    pageItems: {
      immediate: true,
      handler(next) {
        const selected = {};
        next.map(({ _id }) => {
          selected[_id] = this.selected[_id] || false;
        });
        this.selected = selected;
      },
    },
  },
};
</script>

<style scoped>
.update-phones-header {
  position: relative;
}

.update-phones-close-button {
  position: absolute;
  top: -32px;
  right: -32px;
}

.update-phones-actions-title {
  width: 350px;
}

.update-phones-actions-field {
  width: 280px;
}

.update-phones-actions-updated {
  width: 180px;
}

.update-phones-actions-actions {
  width: 110px;
}

.update-phones-page-button {
  width: 55px;
}

.update-phones-page-buttons-before,
.update-phones-page-buttons-after {
  width: 190px;
}

.update-phones-page-selector {
  height: 38px;
  padding-right: 2px;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/UpdatePhones/index.vue